import { useState } from 'react';
import ClockIcon from '@/components/icons/ClockIcon';
import InformationCircle from '@/components/icons/InformationCircle';
import { FormInputProps } from '@/@types/form.types';
import { Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuCheckboxItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

type Props<T extends {}> = { step?: number } & FormInputProps<T>;

const generateTimeOptions = (step: number = 30) => {
	const timeSteps: string[] = [];
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += step) {
			const formattedHour = hour.toString().padStart(2, '0');
			const formattedMinute = minute.toString().padStart(2, '0');
			timeSteps.push(`${formattedHour}:${formattedMinute}`);
		}
	}
	return timeSteps;
};

const ETimePicker = <T extends {}>({
	label,
	fieldName,
	control,
	disabled,
	placeholder = 'Select time',
	step,
}: Props<T>) => {
	const [isOpen, setIsOpen] = useState(false);

	const timeOptions = generateTimeOptions(step);

	return (
		<div className='w-full flex flex-col gap-1.5'>
			{label && <h4 className='text-sm font-medium'>{label}</h4>}
			<Controller
				name={fieldName}
				control={control}
				render={({
					field: { value, onChange },
					fieldState,
					formState,
				}) => {
					const handleSelect = (time: string) => {
						onChange(time);
						setIsOpen(false);
					};

					return (
						<DropdownMenu
							onOpenChange={setIsOpen}
							open={isOpen}>
							<DropdownMenuTrigger asChild>
								<Button
									variant={'outline'}
									className={cn(
										'px-3.5 rounded-semi-xl justify-between text-xs transition',
										fieldState.invalid
											? 'border-error-1000'
											: '',
										isOpen ? 'border-popover-1000' : '',
									)}
									disabled={disabled || formState.disabled}>
									{value ? (
										value
									) : (
										<span className='text-popover-800'>
											{placeholder}
										</span>
									)}
									<ClockIcon />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className={
									'w-(--radix-dropdown-menu-trigger-width) p-0 rounded-semi-xl border border-popover-100 bg-popover-10'
								}>
								<div className='h-fit max-h-56 overflow-y-scroll no-scrollbar'>
									{timeOptions.map((time) => (
										<div
											key={time}
											onClick={() => handleSelect(time)}
											className='px-3.5 py-2 text-xs text-popover-800 cursor-pointer hover:bg-primary-color-50 select-none'>
											{time}
										</div>
									))}
								</div>
							</DropdownMenuContent>
							{fieldState.invalid && (
								<div className='flex items-center gap-1.5'>
									<InformationCircle className='size-4 shrink-0 stroke-error-1000' />
									<span className='text-xs text-error-1000'>
										{fieldState.error?.message}
									</span>
								</div>
							)}
						</DropdownMenu>
					);
				}}
			/>
		</div>
	);
};

export default ETimePicker;
